import { createFileRoute, useParams } from '@tanstack/react-router';
import { RefreshCw } from 'lucide-react';

import { testIds } from '@/shared/testing';

import { useCohortSummary } from '@/features/cohort';
import { useLtvSummary } from '@/features/ltv';
import { usePurchaseCycleSummary } from '@/features/purchase-cycle';
import { useRfmSummary } from '@/features/rfm';
import { useStartInitialSync, useSyncStatus } from '@/features/sync';
import { AnimatedContainer } from '@/shared/ui/animated';

import { AnalysisSummary } from './-components/analysis-summary';
import { AppLayout } from './-components/app-layout';
import { HealthHero } from './-components/health-hero';
import { InsightCards } from './-components/insight-cards';

export const Route = createFileRoute('/_authenticated/$mallId/')({
  component: DashboardPage,
});

function EmptyState({ mallId }: { mallId: string }) {
  const { mutate: startSync, isPending } = useStartInitialSync();

  return (
    <AnimatedContainer variant="scale-in">
      <div
        className="flex flex-col items-center justify-center py-20"
        data-testid={testIds.dashboard.emptyState}
      >
        <div className="rounded-2xl bg-card p-10 shadow-sm text-center max-w-sm">
          <div className="w-14 h-14 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-5">
            <RefreshCw className="w-6 h-6 text-muted-foreground" />
          </div>
          <h2 className="text-lg font-semibold mb-2">아직 분석할 데이터가 없어요</h2>
          <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
            주문 데이터를 동기화하면
            <br />
            고객 건강 상태를 바로 확인할 수 있어요
          </p>
          <button
            type="button"
            onClick={() => startSync({ mallId })}
            disabled={isPending}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 active:scale-95 disabled:opacity-50 transition-all"
            data-testid={testIds.dashboard.syncStartButton}
          >
            <RefreshCw className="w-4 h-4" />
            동기화 시작하기
          </button>
        </div>
      </div>
    </AnimatedContainer>
  );
}

function DashboardPage() {
  const { mallId } = useParams({ from: '/_authenticated/$mallId/' });

  const { data: syncStatus } = useSyncStatus(mallId);
  const hasData = syncStatus?.lastSyncAt != null;

  const { data: rfmSummary, isLoading: rfmLoading } = useRfmSummary();
  const { data: cohortSummary, isLoading: cohortLoading } =
    useCohortSummary('first_purchase_month');
  const { data: purchaseCycleSummary, isLoading: pcLoading } = usePurchaseCycleSummary();
  const { data: ltvSummary, isLoading: ltvLoading } = useLtvSummary();

  const isLoading = rfmLoading || cohortLoading || pcLoading || ltvLoading;

  return (
    <AppLayout pageTitle="대시보드">
      {!hasData && !isLoading ? (
        <EmptyState mallId={mallId} />
      ) : (
        <div className="max-w-4xl mx-auto space-y-6 py-4">
          <AnimatedContainer delay={0}>
            <HealthHero data={rfmSummary} isLoading={rfmLoading} />
          </AnimatedContainer>
          <AnimatedContainer delay={0.1}>
            <InsightCards
              purchaseCycle={purchaseCycleSummary}
              cohort={cohortSummary}
              ltv={ltvSummary}
              isLoading={isLoading}
            />
          </AnimatedContainer>
          <AnimatedContainer delay={0.2}>
            <AnalysisSummary
              rfm={rfmSummary}
              cohort={cohortSummary}
              purchaseCycle={purchaseCycleSummary}
              ltv={ltvSummary}
              isLoading={isLoading}
            />
          </AnimatedContainer>
        </div>
      )}
    </AppLayout>
  );
}
